import React from 'react';
import _ from 'lodash';
import uuid4 from 'uuid/v4';
import ReactTooltip from 'react-tooltip';
import classNames from 'classnames';

import {formatDateOrEmptyString} from 'utils/datetime';

import Table from 'components/Table';
import Avatar from 'components/Avatar';
import Tooltip from 'components/Tooltip';
import ValueOrInfinity from 'components/ValueOrInfinity';
import ClickIconWithNoPropagation from 'components/ClickIconWithNoPropagation';
import {Paginator} from './components';

import bathTubIcon from './assets/bath-thub.svg';
import kitchenIcon from './assets/kitchen.svg';

import './styles.css';

const TCOO_LOCATION_REF = 'tcoo';

const CurrentPrimaryTenant = ({tenant}) => (
  <div className="tenant colab-centered">
    <Avatar
      className="avatar"
      url={tenant.internalAvatar || tenant.avatar}
      initials={tenant.initials}
    />
    <div className="name colab-bold">{tenant.fullName}</div>
  </div>
);

const NeighboursTooltip = () => (
  <Tooltip
    place="bottom"
    type="light"
    effect="solid"
    id="Neighbours__tooltip"
    className="Neighbours__tooltip"
    globalEventOff="click"
    getContent={stringifiedData => {
      const neighbourRoom = stringifiedData && JSON.parse(stringifiedData);
      if (_.isEmpty(neighbourRoom)) {
        return;
      }

      const buildTenant = tenant => (
        <div className="tenant">
          <Avatar
            className="avatar"
            url={tenant.internalAvatar || tenant.avatar}
            initials={tenant.initials}
          />
          <div className="info">
            <p className="name">{tenant.fullName}</p>
            <div className="extraInfo">
              <p>Gender: {tenant.gender || 'N/A'}</p>
              <p>Age: {tenant.age || 'N/A'}</p>
            </div>
          </div>
        </div>
      );

      return (
        <React.Fragment>
          <div className="roomBox">
            <p className="roomNumber">{neighbourRoom.roomNumber}</p>
            {neighbourRoom.currentPrimaryTenant &&
              buildTenant(neighbourRoom.currentPrimaryTenant)}
            {neighbourRoom.currentSecondaryTenant &&
              buildTenant(neighbourRoom.currentSecondaryTenant)}
          </div>
        </React.Fragment>
      );
    }}
  />
);

class Neighbours extends React.Component<any> {
  state = {id: uuid4(), neighboursReceived: false};

  componentDidUpdate(_prevProps, prevState) {
    const {neighbourRoom} = this.props;

    if (!_.isNil(neighbourRoom) && !prevState.neighboursReceived) {
      this.setState({neighboursReceived: true}, ReactTooltip.rebuild);
    }
  }

  buildNeighboursAvatar = neighbour =>
    neighbour &&
    neighbour.currentPrimaryTenant && (
      <Avatar
        className="avatar"
        url={
          neighbour.currentPrimaryTenant.internalAvatar ||
          neighbour.currentPrimaryTenant.avatar
        }
        initials={neighbour.currentPrimaryTenant.initials}
      />
    );

  buildNeighboursRoomNumber = neighbour => {
    if (!neighbour) {
      return <p>N/A</p>;
    }

    return <p>{neighbour.roomNumber}</p>;
  };

  buildNeighboursTenants = neighbour => {
    if (!neighbour || !neighbour.currentPrimaryTenant) {
      return <p>N/A</p>;
    }

    return (
      <p>
        {neighbour.currentPrimaryTenant.fullName}
        {neighbour.currentSecondaryTenant &&
          ` & ${neighbour.currentSecondaryTenant.fullName}`}
      </p>
    );
  };

  render() {
    const {neighbourRoom} = this.props;

    return (
      <React.Fragment>
        <div
          className="neighbours colab-centered-v"
          data-for="Neighbours__tooltip"
          data-event="click focus"
          data-tip={JSON.stringify(neighbourRoom)}>
          <React.Fragment>
            <div className="avatarsRow colab-centered-h">
              {this.buildNeighboursAvatar(neighbourRoom)}
            </div>
            <div className="info">
              <div>{this.buildNeighboursRoomNumber(neighbourRoom)}</div>
              <div>{this.buildNeighboursTenants(neighbourRoom)}</div>
            </div>
          </React.Fragment>
        </div>
      </React.Fragment>
    );
  }
}

const AvailabilityTableRow = ({
  room,
  neighbourRoom,
  showNeighboursColumn,
  showSharingColumn,
  checkViewNeighboursPermission,
  hasViewNeighboursPermission,
  openSalesNotesModal
}) => {
  const hasNeighbourRoom = !_.isNil(neighbourRoom);
  const availableStatusEndDate = formatDateOrEmptyString(
    room.roomAvailableStatusEnd
  );

  return (
    <Table.Row key={room.id}>
      <Table.Cell className="roomTypeColumn colab-bold">
        {room.roomTypeName}
      </Table.Cell>
      <Table.Cell className="roomNumberColumn">{room.roomNumber}</Table.Cell>
      <Table.Cell className="roomPurposeColumn">{room.roomPurpose}</Table.Cell>
      <Table.Cell className="roomStatusColumn">{room.roomStatus}</Table.Cell>
      <Table.Cell className="salesNotes colab-clickable">
        <ClickIconWithNoPropagation
          onClick={() => openSalesNotesModal({relatedObjectId: room.id})}>
          <div className="textContainer">{room.notesText}</div>
        </ClickIconWithNoPropagation>
      </Table.Cell>
      <Table.Cell className="currentBooking">
        {_.get(room, 'currentPrimaryTenant.fullName') && (
          <CurrentPrimaryTenant tenant={room.currentPrimaryTenant} />
        )}
        {!_.get(room, 'currentPrimaryTenant.fullName') && '-'}
      </Table.Cell>
      <Table.Cell
        className={classNames('nextAvailability', {
          'nextAvailability--past': room.nextAvailabilityInThePast
        })}>
        {formatDateOrEmptyString(room.nextAvailableStatusStart)}
      </Table.Cell>
      <Table.Cell className="nextBookingStart">
        {formatDateOrEmptyString(room.nextBookingStart) || '-'}
      </Table.Cell>
      <Table.Cell className="nextAvailableFor">
        {formatDateOrEmptyString(room.nextAvailableStatusStart)} -{' '}
        <ValueOrInfinity value={availableStatusEndDate} />
      </Table.Cell>
      {showSharingColumn && (
        <Table.Cell className="sharing">
          {room.sharesKitchen && <img src={kitchenIcon} alt="Kitchen Icon" />}
          {room.sharesBathroom && <img src={bathTubIcon} alt="Bath tub Icon" />}
        </Table.Cell>
      )}
      {showNeighboursColumn &&
        hasNeighbourRoom && (
          <Table.Cell
            className="roomNeighbours"
            onClick={checkViewNeighboursPermission}>
            <Neighbours
              neighbourRoom={neighbourRoom}
              hasViewNeighboursPermission={hasViewNeighboursPermission}
            />
          </Table.Cell>
        )}
      {showNeighboursColumn &&
        !hasNeighbourRoom && (
          <Table.Cell className="roomNeighbours">
            <div className="neighbours">N/A</div>
          </Table.Cell>
        )}
    </Table.Row>
  );
};

const AvailabilityTable = ({
  exportRoomsAvailabilityCsvInBackground,
  exportingRoomsAvailabilityCsv,
  roomsAvailability,
  roomIdToNeighbourRoom,
  checkViewNeighboursPermission,
  hasViewNeighboursPermission,
  locationRef,
  pagination,
  onPageChange,
  openSalesNotesModal,
  sort,
  sortBy
}) => {
  const showSharingColumn = locationRef === TCOO_LOCATION_REF;
  const showNeighboursColumn = locationRef === TCOO_LOCATION_REF;

  return (
    <div className="AvailabilityTable">
      <Table>
        <Table.Header>
          <Table.HeaderItem className="roomTypeColumn" heading="Room Type" />
          <Table.HeaderItem className="roomNumberColumn" heading="Room No" />
          <Table.HeaderItem
            className="roomPurposeColumn"
            heading="Room Purpose"
          />
          <Table.HeaderItem
            className="roomStatusColumn"
            heading="Room Status"
          />
          <Table.HeaderItem className="salesNotes" heading="Sales Notes" />
          <Table.HeaderItem
            className="currentBooking"
            heading="Current Booking"
          />
          <Table.HeaderItem
            className="nextAvailability"
            heading="Available From"
          />
          <Table.HeaderItem
            className="nextBookingStart"
            heading="Next Booking Start"
            sortDirection={
              sort.field === 'nextBookingStart' ? sort.direction : null
            }
            onSort={() => sortBy('nextBookingStart')}
          />
          <Table.HeaderItem
            className="nextAvailableFor"
            heading="Available For"
          />
          {showSharingColumn && (
            <Table.HeaderItem className="sharing" heading="Sharing" />
          )}
          {showNeighboursColumn && (
            <Table.HeaderItem className="roomNeighbours" heading="Neighbours" />
          )}
        </Table.Header>
      </Table>
      <div className="AvailabilityTable__content">
        <Table>
          <Table.Body>
            {roomsAvailability.map(room => (
              <AvailabilityTableRow
                key={room.id}
                room={room}
                neighbourRoom={_.get(roomIdToNeighbourRoom, room.id)}
                showNeighboursColumn={showNeighboursColumn}
                showSharingColumn={showSharingColumn}
                checkViewNeighboursPermission={checkViewNeighboursPermission}
                hasViewNeighboursPermission={hasViewNeighboursPermission}
                openSalesNotesModal={openSalesNotesModal}
              />
            ))}
          </Table.Body>
        </Table>
      </div>
      <Paginator
        pagination={pagination}
        onPageChange={onPageChange}
        exportRoomsAvailabilityCsvInBackground={
          exportRoomsAvailabilityCsvInBackground
        }
        exportingRoomsAvailabilityCsv={exportingRoomsAvailabilityCsv}
        disableExporting={_.isEmpty(roomsAvailability)}
      />
      {hasViewNeighboursPermission && <NeighboursTooltip />}
    </div>
  );
};

export default AvailabilityTable;
